"use client";

import * as React from "react";
import { Info } from "lucide-react";
import { cn } from "../../lib/utils";
import type { InfoTooltipProps } from "./info-tooltip.types";

export function InfoTooltip({
  content,
  trigger,
  linkHref,
  linkLabel = "Learn more",
  className,
  contentClassName,
  align = "left",
  position = "top",
}: InfoTooltipProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const tooltipId = React.useId();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const [dynamicAlign, setDynamicAlign] = React.useState<
    "left" | "right" | "center"
  >(align);

  // Close tooltip on click or touch outside
  React.useEffect(() => {
    if (!isOpen) return;

    const handleOutsideInteraction = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideInteraction);
    document.addEventListener("touchstart", handleOutsideInteraction);

    return () => {
      document.removeEventListener("mousedown", handleOutsideInteraction);
      document.removeEventListener("touchstart", handleOutsideInteraction);
    };
  }, [isOpen]);

  React.useLayoutEffect(() => {
    if (!isOpen) {
      setDynamicAlign(align);
      return;
    }

    const checkOverflow = () => {
      if (!tooltipRef.current) return;
      const rect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth =
        typeof window !== "undefined" ? window.innerWidth : 0;

      if (rect.right > viewportWidth && rect.left < 0) {
        setDynamicAlign("center");
      } else if (rect.right > viewportWidth) {
        setDynamicAlign("right");
      } else if (rect.left < 0) {
        setDynamicAlign("left");
      }
    };

    // Run layout check synchronously
    checkOverflow();
  }, [isOpen, align]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen((prev) => !prev);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative inline-flex items-center select-none z-45 pointer-events-auto",
        className,
      )}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={trigger ? () => setIsOpen(true) : undefined}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setIsOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") setIsOpen(false);
      }}
    >
      {trigger ? (
        React.cloneElement(trigger, {
          "aria-describedby": isOpen
            ? [trigger.props["aria-describedby"], tooltipId]
                .filter(Boolean)
                .join(" ")
            : trigger.props["aria-describedby"],
        })
      ) : (
        <button
          type="button"
          onClick={handleToggle}
          className="flex items-center justify-center text-[#98A2B3] hover:text-[#344054] active:text-[#101828] transition-colors focus:outline-none p-0.5 rounded cursor-pointer"
          aria-label="Info"
          aria-describedby={isOpen ? tooltipId : undefined}
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      )}

      {isOpen && (
        <div
          ref={tooltipRef}
          role="tooltip"
          id={tooltipId}
          className={cn(
            "absolute z-50 w-60 max-w-[85vw] whitespace-normal rounded-lg bg-[#101828] p-3 text-left text-xs font-normal leading-relaxed text-white shadow-xl animate-in fade-in duration-150 pointer-events-auto",
            position === "top"
              ? "bottom-full mb-2 slide-in-from-bottom-1"
              : "top-full mt-2 slide-in-from-top-1",
            dynamicAlign === "left" && "left-0",
            dynamicAlign === "right" && "right-0",
            dynamicAlign === "center" && "left-1/2 -translate-x-1/2",
            contentClassName,
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {content}
          {linkHref ? (
            <a
              href={linkHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 block font-medium text-[#9DB2F5] underline underline-offset-2 hover:text-white"
              onClick={(e) => e.stopPropagation()}
            >
              {linkLabel}
            </a>
          ) : null}
          <div
            className={cn(
              "absolute border-4 border-transparent",
              position === "top"
                ? "top-full -translate-y-px border-t-[#101828]"
                : "bottom-full translate-y-px border-b-[#101828]",
              dynamicAlign === "left" && "left-3",
              dynamicAlign === "right" && "right-3",
              dynamicAlign === "center" && "left-1/2 -translate-x-1/2",
            )}
          />
        </div>
      )}
    </div>
  );
}
